import {
  createStyles,
  FormControl,
  FormControlLabel,
  makeStyles,
  Radio,
  RadioGroup,
  Theme,
  Typography,
} from '@material-ui/core';
import React, { Fragment, useState } from 'react';
import { useIsMobile } from '../../../../util/style-utils';
import { useTranslation } from '../../../../hooks/useTranslation';
import colors from '../../../../themes/main-colors';
import {
  OutageLookupParams,
  OutageLookUpType,
  PremiseInfo,
} from '../../../../__generated__/pge-types';
import { CustomDialog } from '../common/custom-dialog';
import { CustomButtons } from '../common/footer-buttons/ButtonsComp';
import {
  OutageMarkerView,
  useOutageLeftPanelState,
} from '../../providers/OutageLeftPanelStateProvider';
import { useOutageLookUp } from '../../hooks/useOutageLookUp';
import {
  ResponsivePageView,
  useOutageResponsivePageTracker,
} from '../../providers/OutageResponsivePageTracker';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    subHeading: {
      fontSize: 16,
      marginTop: 5,
    },
    radioGroup: {
      marginTop: '1rem',
      background: colors.white,
      padding: '1rem',
      maxHeight: '45vh',
      overflow: 'auto',
      scrollbarWidth: 'none',
    },
    bodyTextSmall: {
      fontSize: 14,
      marginBottom: '1rem',
    },
  }),
);

const MultiPremisePopup = () => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const {
    panelOpen: expanded,
    multiPremiseInfo,
    setOutageMarkerView,
    setReportOutageOpen,
    accountNotFoundInfo,
    setAccountNotFoundInfo,
    setMultiPremiseInfo,
    setSinglePremiseInfo,
    setHouseNumber,
  } = useOutageLeftPanelState();
  const phoneNumber = t('CUSTOMER_CARE_NUMBER_OTHER');
  const [premiseChecked, setPremiseChecked] = useState<boolean>(false);
  const [premiseHouseNumber, setPremiseHouseNumber] = useState<
    string | undefined
  >();
  const [premiseEncryptedValue, setPremiseEncryptedValue] = useState<string>(
    '',
  );
  const [selectedPremise, setSelectedPremise] = useState<PremiseInfo | null>(
    null,
  );
  const premiseList = multiPremiseInfo?.MultiPremiseList;
  const isMultiPremiseModalOpen = multiPremiseInfo?.isMultiSelectModalOpen;
  const { getOutageLookUpQueryResponse } = useOutageLookUp();
  const { setPage } = useOutageResponsivePageTracker();
  const onHouseNumberChecked = (e: any) => {
    const selectedVal = JSON.parse(e.target.value);
    setPremiseEncryptedValue(selectedVal.encryptedValue);
    setPremiseChecked(true);
    setPremiseHouseNumber(selectedVal.houseNumber);
    setSelectedPremise(selectedVal.premise);
  };

  const handleOnSubmit = async () => {
    if (premiseChecked && premiseEncryptedValue) {
      const params: OutageLookupParams = {
        lookupType: OutageLookUpType.Premise,
        encryptedValue: premiseEncryptedValue,
      };
      setHouseNumber('');
      setMultiPremiseInfo({
        isMultiPremiseAccount: false,
        MultiPremiseList: undefined,
        isMultiSelectModalOpen: false,
        selectedPremiseDetails: undefined,
        selectedPremiseEncryptedValue: undefined,
      });
      const response = await getOutageLookUpQueryResponse(params);
      if (isMobile) {
        setPage(ResponsivePageView.Details);
      }
      if (response?.outages?.[0].hasKnownOutage) {
        setOutageMarkerView(OutageMarkerView.SearchedLocation);
      } else {
        setOutageMarkerView(OutageMarkerView.NoOutage);
      }
      setSinglePremiseInfo(undefined);
      const currentMulti = multiPremiseInfo;
      const updatedMulti = currentMulti
        ? {
            ...currentMulti,
            isMultiSelectModalOpen: false,
            selectedPremiseDetails: response,
            selectedPremiseEncryptedValue: premiseEncryptedValue,
          }
        : {
            isMultiPremiseAccount: false,
            MultiPremiseList: undefined,
            isMultiSelectModalOpen: false,
            selectedPremiseDetails: undefined,
            selectedPremiseEncryptedValue: undefined,
          };
      setMultiPremiseInfo(updatedMulti);
    }
  };

  const onHandleClose = () => {
    const current = multiPremiseInfo;
    const updated = current
      ? { ...current, isMultiSelectModalOpen: false }
      : {
          isMultiPremiseAccount: false,
          MultiPremiseList: undefined,
          isMultiSelectModalOpen: false,
          selectedPremiseDetails: undefined,
          selectedPremiseEncryptedValue: undefined,
        };
    setMultiPremiseInfo(updated);
  };

  return (
    <Fragment>
      <CustomDialog
        open={isMultiPremiseModalOpen || false}
        onClose={() => onHandleClose()}
        title={t('SELECT_SERVICE_ADDRESS')}
        titleBgColor={isMobile ? '' : colors.riverBlue}
        titleTextColor={isMobile ? '' : colors.noirBlur}
      >
        <div css={{ padding: '1rem' }}>
          <Typography className={classes.subHeading}>
            {t('MULTIPLE_SERVICE_ADDRESS_TEXT')}
          </Typography>
          <div className={classes.radioGroup}>
            {!isMobile && (
              <Typography variant={'body1'} className={classes.bodyTextSmall}>
                {t('PGE_CUSTOMER_CALL_IF_NOT_ABLE_IDENTIFY_OUTAGE_LOCATION')}{' '}
                {
                  <a
                    href={'tel:' + phoneNumber}
                    style={{ textDecoration: 'none', color: colors.brightBlue }}
                  >
                    {' '}
                    <strong>{phoneNumber}</strong>
                  </a>
                }
              </Typography>
            )}

            <FormControl component="fieldset">
              <RadioGroup
                aria-label={'report-outage-multiple-addresses'}
                name={'selectAddressForOutage'}
                onChange={e => onHouseNumberChecked(e)}
              >
                {Array.isArray(premiseList)
                  ? premiseList.map(premise => {
                      return (
                        <FormControlLabel
                          value={JSON.stringify({
                            encryptedValue: premise?.encryptedValue,
                            houseNumber: premise?.houseNumber,
                            premise: premise,
                          })}
                          control={<Radio color={'primary'} />}
                          label={
                            <div css={{ fontSize: '16px' }}>
                              {premise?.houseNumber +
                                ' ' +
                                premise?.last4CharAddress}
                              {/* Comment out this piece of code until further instructions.
                              {premise?.servicePointDetails?.find(
                                (servicePoint: ServicePointDetail) =>
                                  servicePoint?.outageInfo?.hasKnownOutage,
                              ) && (
                                <Typography
                                  component="span"
                                  css={{
                                    color: colors.errorRed,
                                    marginLeft: '12px',
                                    fontSize: '0.8rem',
                                    lineHeight: '0.8rem',
                                    fontWeight: 'bold',
                                  }}
                                >
                                  (Outage Reported)
                                </Typography>
                              )} */}
                            </div>
                          }
                        />
                      );
                    })
                  : null}
              </RadioGroup>
            </FormControl>
          </div>
          <CustomButtons
            buttonOne={t('CANCEL')}
            buttonOneOnClick={() => onHandleClose()}
            buttonTwo={isMobile ? t('FIND_ADDRESS') : t('SHOW_DETAILS')}
            buttonTwoOnClick={() => handleOnSubmit()}
          />
        </div>
      </CustomDialog>
    </Fragment>
  );
};

export default MultiPremisePopup;
